import { 
  ChatConversation, 
  CopilotItem, 
  ChatMessage, 
  GraphQLCopilotResponse 
} from '@/types/copilot-chat';
import { 
  transformGraphQLResponse, 
  isGraphQLCopilotResponse 
} from './copilotGraphQLService';
import { authClient } from '@/config/auth';

const apiUrl = import.meta.env.VITE_REACT_APP_API_URL;
const CONVERSATIONS_API_BASE_URL = `${apiUrl}/copilot`;

// API Request/Response interfaces
export interface CreateConversationRequest {
  title: string;
  userId: string;
  copilotProfileId?: string;
  initialMessage?: {
    content: string;
    type: 'message' | 'action_card';
    sender: {
      id: string;
      name: string;
    };
    isAI: boolean;
    metadata?: Record<string, any>;
  };
}

export interface CreateConversationResponse {
  data: ChatConversation;
  message: string;
  status: string;
}

export interface AddMessageRequest {
  conversationId: string;
  content: string;
  type: 'message' | 'action_card';
  sender: {
    id: string;
    name: string;
  };
  isAI: boolean;
  metadata?: Record<string, any>;
}

export interface AddMessageResponse {
  data: CopilotItem;
  message: string;
  status: string;
}

export interface GetConversationResponse {
  data: ChatConversation;
  message: string;
  status: string;
}

export interface GetConversationsResponse {
  data: ChatConversation[];
  message?: string;
  status?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Backend response format (what we actually get)
export interface ConversationsListResponse {
  success: boolean;
  conversations: ConversationsListItem[];
  count: number;
  limit?: number;
  message?: string;
}

// Backend conversation format for new conversations endpoint
interface ConversationsListItem {
  id: string;
  thread_id?: string | null;
  title: string | null;
  title_version?: number;
  status?: string | null;
  message_count?: number;
  last_message_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  copilot_profile_id?: string | null;
  copilot_profile_name?: string | null;
  workspace_id?: string | null;
  team_id?: string | null;
  profile_name?: string | null;
  archived?: boolean;
}

export interface ConversationSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  showArchived?: boolean;
  copilotProfileId?: string;
}

/**
 * Simple API functions for copilot conversation history
 * Each function accepts getToken for authentication
 */

/**
 * Make authenticated request to copilot backend
 */
async function makeAuthenticatedRequest<T>(
  endpoint: string,
  getToken: () => Promise<string | null>,
  options: RequestInit = {}
): Promise<T> {
  try {
    // const token = await getToken();
    // if (!token) {
    //   throw new Error('No authentication token available');
    // }

    console.log(`🔄 [CopilotHistory] Making request to: ${endpoint}`);
    console.log(`🔄 [CopilotHistory] Full URL: ${CONVERSATIONS_API_BASE_URL}${endpoint}`);
    console.log(`🔄 [CopilotHistory] Request options:`, options);
    
    // Add timeout to detect hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('⏰ [CopilotHistory] Request timeout after 10 seconds');
      controller.abort();
    }, 10000);
    
    const response = await fetch(`${CONVERSATIONS_API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controller.signal,
      credentials: 'include',
    });
    
    clearTimeout(timeoutId);

    console.log(`📡 [CopilotHistory] Response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error');
      console.error(`❌ [CopilotHistory] HTTP Error ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log(`✅ [CopilotHistory] Request successful for ${endpoint}`, data);
    return data;
  } catch (error: any) {
    console.error(`❌ [CopilotHistory] Error in ${endpoint}:`, error);
    throw new Error(error.message || `Failed to make request to ${endpoint}`);
  }
}

/**
 * Create a new copilot conversation
 */
export async function createConversation(
  data: CreateConversationRequest,
  getToken: () => Promise<string | null>
): Promise<CreateConversationResponse> {
  console.log('🆕 [CopilotHistory] Creating new conversation:', data.title);
  return makeAuthenticatedRequest<CreateConversationResponse>(
    '/conversations',
    getToken,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Add a message to an existing conversation
 */
export async function addMessage(
  data: AddMessageRequest,
  getToken: () => Promise<string | null>
): Promise<AddMessageResponse> {
  const { conversationId, ...messageData } = data;
  console.log('💬 [CopilotHistory] Adding message to conversation:', conversationId);
  return makeAuthenticatedRequest<AddMessageResponse>(
    `/conversations/${conversationId}/messages`,
    getToken,
    {
      method: 'POST',
      body: JSON.stringify(messageData),
    }
  );
}

/**
 * Get a specific conversation by ID
 */
export async function getConversation(
  conversationId: string,
  getToken: () => Promise<string | null>
): Promise<GetConversationResponse> {
  console.log('📖 [CopilotHistory] Getting conversation:', conversationId);
  return makeAuthenticatedRequest<GetConversationResponse>(
    `/conversations/${conversationId}`,
    getToken
  );
}

/**
 * Get all conversations for the current user with optional pagination and search
 */
export async function getConversations(
  params: ConversationSearchParams | undefined,
  getToken: () => Promise<string | null>
): Promise<GetConversationsResponse> {
  const queryParams = new URLSearchParams();
  const requestedPage = params?.page ?? 1;
  const requestedLimit = params?.limit ?? 50;

  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.copilotProfileId) queryParams.append('copilot_profile_id', params.copilotProfileId);
  if (params?.showArchived) {
    queryParams.append('include_archived', 'true');
  }

  const endpoint = `/conversations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const url = `${CONVERSATIONS_API_BASE_URL}${endpoint}`;

  console.log('📚 [CopilotHistory] Fetching conversations from new endpoint:', url);

  // const token = await getToken();
  // if (!token) {
  //   throw new Error('No authentication token available');
  // }

  const session = await authClient.getSession();
  const userId = session.data.user.id
  const workspaceId = localStorage.getItem("workspaceId");


  const response = await fetch(url, {
    method: 'GET',
    headers: {
      "X-User-Id": userId,
      "X-Workspace-Id": workspaceId || "",
      "X-Role": "admin"
    },
    credentials: 'include',
  });

  console.log('📡 [CopilotHistory] New endpoint response status:', response.status);

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unable to read error');
    console.error(`❌ [CopilotHistory] Conversations request failed: ${errorText}`);
    throw new Error(`HTTP error! status: ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  // Backend wraps in { status, message, data: { conversations, total, page, limit } }
  const envelope = data?.data ?? data;
  const listResponse: ConversationsListResponse = {
    ...envelope,
    success: data?.status === 'success' || envelope?.success === true,
    count: envelope?.total ?? envelope?.count ?? 0,
  };
  console.log('✅ [CopilotHistory] Conversations response received:', listResponse);

  if (!listResponse.success) {
    throw new Error(data?.message || listResponse.message || 'Failed to load conversations');
  }

  const transformedConversations: ChatConversation[] = (listResponse.conversations || []).map(conv => {
    const status = conv.archived || conv.status === 'archived' ? 'archived' : 'active';
    const lastMessageAt = conv.updated_at || conv.last_message_at || conv.created_at || new Date().toISOString();

    return {
      id: conv.id,
      thread_id: conv.thread_id ?? conv.id,
      title: conv.title ?? 'Untitled conversation',
      title_version: conv.title_version,
      status,
      message_count: conv.message_count ?? 0,
      last_message_at: lastMessageAt,
      createdAt: conv.created_at || lastMessageAt,
      updatedAt: conv.updated_at || conv.last_message_at || undefined,
      team_id: conv.workspace_id ?? conv.team_id ?? null,
      profile_name: conv.profile_name ?? conv.copilot_profile_name ?? null,
      messages: [],
    };
  });

  const totalCount = typeof listResponse.count === 'number' ? listResponse.count : transformedConversations.length;
  const effectiveLimit = params?.limit ?? listResponse.limit ?? requestedLimit;
  const totalPages = Math.max(1, Math.ceil(totalCount / (effectiveLimit || 1)));

  const transformedResponse: GetConversationsResponse = {
    data: transformedConversations,
    pagination: {
      page: requestedPage,
      limit: effectiveLimit || transformedConversations.length || 1,
      total: totalCount,
      totalPages,
    },
  };

  console.log('🔄 [CopilotHistory] Transformed conversations response:', transformedResponse);
  return transformedResponse;
}

/**
 * Update conversation title
 */
export async function updateConversation(
  conversationId: string,
  data: { title: string },
  getToken: () => Promise<string | null>
): Promise<GetConversationResponse> {
  console.log('✏️ [CopilotHistory] Updating conversation:', conversationId);
  return makeAuthenticatedRequest<GetConversationResponse>(
    `/conversations/${conversationId}`,
    getToken,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Archive a conversation
 */
export async function archiveConversation(
  conversationId: string,
  getToken: () => Promise<string | null>
): Promise<{ message: string; status: string }> {
  console.log('📦 [CopilotHistory] Archiving conversation:', conversationId);
  return makeAuthenticatedRequest<{ message: string; status: string }>(
    `/conversations/${conversationId}/archive`,
    getToken,
    {
      method: 'POST',
    }
  );
}

/**
 * Unarchive a conversation
 */
export async function unarchiveConversation(
  conversationId: string,
  getToken: () => Promise<string | null>
): Promise<{ message: string; status: string }> {
  console.log('📤 [CopilotHistory] Unarchiving conversation:', conversationId);
  return makeAuthenticatedRequest<{ message: string; status: string }>(
    `/conversations/${conversationId}/unarchive`,
    getToken,
    {
      method: 'POST',
    }
  );
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  conversationId: string,
  getToken: () => Promise<string | null>
): Promise<{ message: string; status: string }> {
  console.log('🗑️ [CopilotHistory] Deleting conversation:', conversationId);
  return makeAuthenticatedRequest<{ message: string; status: string }>(
    `/conversations/${conversationId}`,
    getToken,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Create a new conversation with a title generated from the first message
 */
export async function createNewConversation(
  initialMessage: string,
  userId: string,
  getToken: () => Promise<string | null>
): Promise<CreateConversationResponse> {
  const createRequest: CreateConversationRequest = {
    title: initialMessage.length > 50 ? `${initialMessage.substring(0, 50)}...` : initialMessage,
    userId,
    initialMessage: {
      content: initialMessage,
      type: 'message',
      sender: {
        id: userId,
        name: 'You',
      },
      isAI: false,
      metadata: {
        timestamp: new Date().toISOString(),
      },
    },
  };

  return createConversation(createRequest, getToken);
}

/**
 * Add GraphQL copilot response messages to a conversation
 */
export async function addGraphQLMessages(
  conversationId: string,
  graphqlResponse: GraphQLCopilotResponse,
  getToken: () => Promise<string | null>
): Promise<{ messages: ChatMessage[]; success: boolean }> {
  try {
    if (!isGraphQLCopilotResponse(graphqlResponse)) {
      throw new Error('Invalid GraphQL copilot response format');
    }

    const { messages } = transformGraphQLResponse(graphqlResponse);
    const addedMessages: ChatMessage[] = [];

    // Add each message to the conversation
    for (const message of messages) {
      const messageRequest: AddMessageRequest = {
        conversationId,
        content: message.content,
        type: 'message',
        sender: message.sender,
        isAI: message.isAI,
        metadata: {
          timestamp: message.timestamp,
          structured_data: message.structured_data,
          role: message.role,
          graphql_message_id: message.id
        }
      };

      const response = await addMessage(messageRequest, getToken);
      addedMessages.push(response.data as ChatMessage);
    }

    return {
      messages: addedMessages,
      success: true
    };
  } catch (error: any) {
    console.error('❌ [CopilotHistory] Failed to add GraphQL messages:', error);
    return {
      messages: [],
      success: false
    };
  }
}

/**
 * Process GraphQL copilot response and add to conversation
 */
export async function processGraphQLCopilotResponse(
  conversationId: string,
  response: any,
  getToken: () => Promise<string | null>
): Promise<{ messages: ChatMessage[]; threadId?: string; success: boolean }> {
  if (isGraphQLCopilotResponse(response)) {
    const { messages, threadId } = transformGraphQLResponse(response);
    const result = await addGraphQLMessages(conversationId, response, getToken);
    
    return {
      messages: result.messages,
      threadId,
      success: result.success
    };
  }

  // If not a GraphQL response, return empty result
  return {
    messages: [],
    success: false
  };
}
